import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import classes from './AnimatedPet.module.css';
import pets from '../../components/sidebar/pets.json';

type Pet = {
  id: string;
  name: string;
  type: string;
  rarity: string;
};

export default function AnimatedPet(pet: Pet) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [boxState, setBoxState] = useState<'moving' | 'static'>('static');

  const petData = pets.find((p) => p.id === pet?.id);

  const [useAnimated, setUseAnimated] = useState<boolean>(true);
  const bgImageUrl = `url(${useAnimated ? petData?.animate : petData?.img})`;

  const animationClassName: string | undefined =
    boxState === 'moving' ? classes['pet-animate'] : undefined;

  const restartTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    function updateWidth() {
      const width = ref.current?.offsetWidth;
      if (ref.current && typeof width === 'number') {
        ref.current.style.setProperty('--current-width', `${width}px`);
      }
    }

    updateWidth();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setUseAnimated(true);
    setBoxState('moving');

    return () => {
      if (restartTimeoutRef.current) {
        window.clearTimeout(restartTimeoutRef.current);
      }
    };
  }, []);

  const handleAnimationEnd = () => {
    // this is ONLY to start animation, not to stop it
    if (boxState !== 'moving') return;

    setBoxState('static');
    setUseAnimated(false);

    if (restartTimeoutRef.current) {
      window.clearTimeout(restartTimeoutRef.current);
    }
    restartTimeoutRef.current = window.setTimeout(() => {
      setUseAnimated(true);
      setBoxState('moving');
      restartTimeoutRef.current = null;
    }, 4000);
  };

  return petData?.animate ? (
    <Box
      component="div"
      ref={ref}
      sx={{
        height: '300px',
        width: '300px',
        maxWidth: '300px',
        maxHeight: '300px',
        aspectRatio: '1 / 1',
        backgroundImage: bgImageUrl,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }}
      onAnimationEnd={handleAnimationEnd}
      className={animationClassName}
    ></Box>
  ) : (
    <img
      src={petData?.img}
      alt={petData?.name}
      height="300px"
      width="300px"
      className="float-pet"
    />
  );
  // );
}
